/** Precomputed Super Admin Overview money-card aggregates (#1061). */
import { DateTime } from 'luxon';
export const DASHBOARD_ROLLUPS_COLLECTION = 'dashboard_rollups';
export const OVERVIEW_ROLLUP_META_DOC = 'overview';
/** Business timezone for month buckets (matches scheduled Functions). */
export const OVERVIEW_ROLLUP_ZONE = 'America/New_York';
/** YYYY-MM for a calendar month in {@link OVERVIEW_ROLLUP_ZONE}. */
export const overviewMonthPeriodKey = (year, monthIndex0) => {
    const month = String(monthIndex0 + 1).padStart(2, '0');
    return `${year}-${month}`;
};
export const overviewMonthRollupDocId = (periodKey) => `month_${periodKey}`;
export const monthBoundsMs = (year, monthIndex0) => {
    const start = DateTime.fromObject({ year, month: monthIndex0 + 1, day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 }, { zone: OVERVIEW_ROLLUP_ZONE });
    const end = start.endOf('month');
    return { startMs: start.toMillis(), endMs: end.toMillis() };
};
/** Inclusive list of YYYY-MM keys that intersect [startMs, endMs]. */
export const periodKeysOverlappingRange = (startMs, endMs) => {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs)
        return [];
    const keys = [];
    let cursor = DateTime.fromMillis(startMs, { zone: OVERVIEW_ROLLUP_ZONE }).startOf('month');
    const end = DateTime.fromMillis(endMs, { zone: OVERVIEW_ROLLUP_ZONE });
    while (cursor.toMillis() <= end.toMillis()) {
        keys.push(overviewMonthPeriodKey(cursor.year, cursor.month - 1));
        cursor = cursor.plus({ months: 1 });
    }
    return keys;
};
/**
 * True when the range matches one or more full calendar months in
 * {@link OVERVIEW_ROLLUP_ZONE} (within 1s for end-of-month clock skew).
 */
export const rangeIsFullLocalMonths = (startMs, endMs) => {
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs)
        return false;
    const start = DateTime.fromMillis(startMs, { zone: OVERVIEW_ROLLUP_ZONE });
    const end = DateTime.fromMillis(endMs, { zone: OVERVIEW_ROLLUP_ZONE });
    if (start.day !== 1 || start.hour !== 0 || start.minute !== 0 || start.second !== 0) {
        return false;
    }
    const monthEnd = monthBoundsMs(end.year, end.month - 1).endMs;
    return Math.abs(endMs - monthEnd) < 1000;
};
/** @deprecated alias */
export const rangeIsFullUtcMonths = rangeIsFullLocalMonths;
export const emptyOverviewMonthRollup = (periodKey, startMs, endMs) => ({
    periodKey,
    startMs,
    endMs,
    soldCount: 0,
    totalRevenue: 0,
    totalProfit: 0,
    daysToSellSum: 0,
    expenseTotal: 0,
    expensesByCategory: {},
    expenseLinkedVehicleCounts: {},
    updatedAt: new Date().toISOString(),
});
export const sumOverviewMonthRollups = (months) => {
    const expensesByCategory = {};
    const expenseLinkedVehicleCounts = {};
    let soldCount = 0;
    let totalRevenue = 0;
    let totalProfit = 0;
    let daysToSellSum = 0;
    let expenseTotal = 0;
    for (const month of months) {
        soldCount += month.soldCount;
        totalRevenue += month.totalRevenue;
        totalProfit += month.totalProfit;
        daysToSellSum += month.daysToSellSum;
        expenseTotal += month.expenseTotal;
        for (const [key, value] of Object.entries(month.expensesByCategory ?? {})) {
            expensesByCategory[key] = (expensesByCategory[key] ?? 0) + value;
        }
        for (const [key, value] of Object.entries(month.expenseLinkedVehicleCounts ?? {})) {
            expenseLinkedVehicleCounts[key] = (expenseLinkedVehicleCounts[key] ?? 0) + value;
        }
    }
    return {
        soldCount,
        totalRevenue,
        totalProfit,
        daysToSellSum,
        expenseTotal,
        expensesByCategory,
        expenseLinkedVehicleCounts,
    };
};
export const avgDaysToSellFromRollup = (soldCount, daysToSellSum) => soldCount > 0 ? daysToSellSum / soldCount : 0;
/** Profit formula matching Super Admin Overview financial cards. */
export const computeOverviewVehicleProfit = (vehicle) => {
    const sellPrice = vehicle.optic_list_price || 0;
    const buyPrice = vehicle.seller_asking_price || 0;
    const costs = (vehicle.repair_cost || 0) +
        (vehicle.shipping_cost || 0) +
        (vehicle.rental_cost || 0) +
        (vehicle.other_cost || 0);
    return sellPrice - buyPrice - costs;
};
export const daysToSellForVehicle = (vehicle) => {
    if (!vehicle.date_added || !vehicle.sold_date)
        return 0;
    const added = new Date(vehicle.date_added).getTime();
    const sold = new Date(vehicle.sold_date).getTime();
    if (!Number.isFinite(added) || !Number.isFinite(sold))
        return 0;
    return Math.floor((sold - added) / (1000 * 60 * 60 * 24));
};
